<template>
    <vx-collapsible
        class="contact-messages-scheduled"
        :class="{ 'contact-messages-scheduled__closed': !showScheduledMessages }"
        :toggle-msg="showScheduledMessages ? 'Hide Scheduled Messages' : 'View Scheduled Messages'"
        position="top"
        :height="210"
        @toggle="showScheduledMessages = $event"
        v-if="hasMessages"
    >
        <div v-if="isLoading" class="contact-view__spinner">
            <i class="fa fa-circle-o-notch fa-spin"></i>
        </div>

        <template v-else v-for="(message, msgIndex) in messages" :key="message.id">
            <span v-if="shouldShowDateSlug(message, msgIndex)" class="c-chat__divider" :key="message.id + '_date_slug'" data-test="message-date-slug">
                {{ weekDayOfMonth(message.send_at) }}
            </span>

            <vx-message-cloud
                :messageId="message.id"
                :mediaUrl="message.media_url"
                :mediaContentType="message.media_content_type"
                :messageBody="message.body"
                :isScheduled="true"
                :hasEdit="false"
            >
                <template v-slot:info>
                    scheduled for {{timeAmPm(message.send_at)}}
                </template>
            </vx-message-cloud>
        </template>

        <vx-button
            v-if="hasMoreMessages"
            class="contact-view__load-more"
            appearance="transparent-dark-outline"
            size="large"
            :disabled="isLoading"
            @click="loadMessages"
        >
            Load More
        </vx-button>
    </vx-collapsible>
</template>

<script setup>
import { ref, computed } from 'vue';
import { VxCollapsible, VxMessageCloud, VxButton } from '@voxie/frontend-components-v3';
import { useToasted } from '../../../composables/useToasted';
import { timeAmPm, weekDayOfMonth } from '../../filters';
import { scheduledMessagesClient } from '../../../services';
import dayjs from '~/utils/dayjs';

const props = defineProps({
    contact: {
        type: Object,
        required: true,
    },
    perPage: {
        type: Number,
        required: true,
    },
});

const $toasted = useToasted();

const showScheduledMessages = ref(false);
const messages = ref([]);
const pageCursor = ref();
const nextPageUrl = ref(null);
const isLoading = ref(false);

const hasMoreMessages = computed(() => !!nextPageUrl.value);
const hasMessages = computed(() => messages.value.length > 0);

const loadMessages = () => {
    isLoading.value = true;

    const teamId = Spark.state.currentTeam.id;

    const params = {
        cursor: pageCursor.value,
        per_page: props.perPage,
    };

    scheduledMessagesClient
        .getContactScheduledMessages(teamId, props.contact.id, params)
        .then(({ data }) => {
            messages.value = [...messages.value, ...data.data];
            nextPageUrl.value = data.next_page_url;
            pageCursor.value = nextPageUrl.value && new URL(nextPageUrl.value).searchParams.get('cursor');
        })
        .catch(() => {
            $toasted.global.platform_error();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const shouldShowDateSlug = (message, index) => {
    if (index == 0) return true;

    let prevMessage = messages.value[index - 1];

    return !!prevMessage && !dayjs(prevMessage.send_at).isSame(dayjs(message.send_at), 'day');
};

loadMessages();
</script>

<style lang="scss">
.contact-messages-scheduled {
    margin-bottom: 24px;

    .vx-collapsible__toggle {
        font-family: var(--font-dm-sans);
        font-weight: var(--weight-medium);
        font-size: 0.9375rem; // 15px
    }
}

.contact-messages-scheduled__closed {
    .vx-collapsible__toggle {
        padding-top: 0;
    }
}
</style>
